import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@repo/ui'

import { useSnackbar } from 'contexts'
import { ROUTES } from 'routes'

import { useChecklistVisibility } from './useChecklistVisibility'

/**
 * Announce a setup step completing, from wherever the user happens to be (VRG-340).
 *
 * THE PROBLEM THIS SOLVES. Every checklist step is completed somewhere else — you add a
 * product on Products, a payment method in Billing. So the one moment the progress changes is
 * the one moment Home is not on screen, and without this the user has no idea the step
 * counted or that there is a list to go back to.
 *
 * WHY A TOAST IS ALLOWED HERE. The toast policy says a success-of-a-VISIBLE-action gets no
 * toast — creating a certificate already shows you the certificate, so it needs none. This is
 * not that. "Your setup went 3 of 6 to 4 of 6" is an INVISIBLE side effect of that action,
 * happening on a surface the user cannot see, which is precisely the case the policy reserves
 * toasts for. It pairs with the persistent count on the sidebar Home row: the badge answers
 * "how far am I" at any time, the toast answers "did that count" at the moment it changes.
 *
 * WHY THIS IS A DIFF AND NOT AN EVENT. The checklist is DERIVED from six queries and there is
 * deliberately no stored completion flag — its derivation IS the source of truth. So there is
 * no event to subscribe to, and the transition has to be observed by comparing the previous
 * derived value against the current one.
 *
 * That makes the first-load guard the whole difficulty rather than a detail. On a cold load
 * every item transitions from "not known yet" to its real value, so a naive diff fires a toast
 * for each already-completed step the moment the page opens. `seeded` therefore records the
 * FIRST settled reading without announcing it, and only differences measured against that
 * baseline are announced. The ref also has to survive re-renders without causing them, which
 * is why it is a ref and not state.
 *
 * Mounted once, in Layout, because the queries it reads are already running app-wide:
 * `useChecklistVisibility` is called by the Sidebar on every page, so this adds no requests.
 */
export const useChecklistProgressToast = () => {
  const { open } = useSnackbar()
  const navigate = useNavigate()
  const { checklist, isChecklistLive } = useChecklistVisibility()
  /*
    ⚠️ CLAIMING THE REWARD USED TO LIVE HERE, AND THAT WAS THE BUG. It is now a BUTTON in the
    reward banner (`SetupChecklist.tsx`). This hook only announces progress.

    The old argument was that this is already the one place that detects the checklist
    COMPLETING, so a second hook would be a second diff of the same derived state with its own
    first-load guard to get wrong. The premise was true and the conclusion did not follow,
    because the very guard that makes this hook correct for TOASTS makes it wrong for a GRANT:

      `baseline` deliberately swallows the FIRST settled reading (see its note below) — that
      is what stops a cold load firing one toast per already-finished step. So the claim only
      ever fired for a user who was mounted and watching at the instant the count changed.
      Complete the last step and then reload, or navigate to Home fresh, and the first settled
      reading is ALREADY complete: baseline becomes that value, `current <= baseline` returns
      early, and the grant is never attempted — on that load or on any load after it, forever.

    Verified, not theorised: `company_activation_ledger` held ZERO rows for a company whose
    banner had been sitting on "Activation granted shortly…" indefinitely. A credit the user
    earned and could never receive.

    Suppressing a duplicate ANNOUNCEMENT and suppressing a WRITE are different risk profiles:
    a missed toast costs nothing, a missed grant costs the user money. A grant needs a trigger
    that cannot be missed by an observer that happened not to be mounted — an explicit click
    is that trigger, and it also gives the user something to do instead of a sentence promising
    a credit that never arrives.
  */

  // The last SETTLED completed-count, or null until the first settled reading is recorded.
  const baseline = useRef<number | null>(null)

  useEffect(() => {
    // Nothing to compare against while the six queries are still resolving — a mid-flight
    // reading is not a state the user was ever in.
    if (checklist.isLoading) return

    const current = checklist.completedCount

    // First settled reading: remember it, announce nothing. This is the guard that stops a
    // cold load firing one toast per already-finished step.
    if (baseline.current === null) {
      baseline.current = current
      return
    }

    if (current <= baseline.current) {
      // Also covers the count going DOWN — a product deleted, a payment method removed. That
      // is not something to congratulate, so it silently re-baselines.
      baseline.current = current
      return
    }

    baseline.current = current

    // The final step retires the CHECKLIST. Home itself stays (and so does its sidebar entry),
    // but there is no longer a setup list to send anyone back to, so the link is dropped.
    const finished = current === checklist.total

    open({
      // `info`, not `success`. Nothing succeeded or failed here — a step advanced — and the
      // toast convention reserves the success treatment for the outcome of an action the user
      // just took. This reports a side effect of one.
      type: 'info',
      /*
        Longer than the 5s default, and longer still when it carries the "View setup" action.
        5s is tuned for a message you only read; this one asks the reader to notice it, read a
        two-line update, decide, and travel to a button — and it fires while their attention is
        on a DIFFERENT page, because every step is completed somewhere other than Home.

        The completion toast gets the most time: it is the one that announces the reward, and
        it is the single message in this flow that is genuinely bad to miss.
      */
      durationMs: finished ? 12000 : 9000,
      title: finished ? 'Setup complete' : `Setup progress: ${current} of ${checklist.total}`,
      // Deliberately says the activation is OFFERED, not that it has landed. Claiming is a
      // button on the card now (see the note at the top of this hook), so a toast asserting
      // "granted" would be the same false receipt the banner's old "Activation granted
      // shortly…" copy was. This sends them to the card; the card does the crediting.
      text: finished
        ? 'That is everything. Your free activation is waiting on Home — claim it there.'
        : 'That step is ticked off your setup checklist.',
      // `action` is a ReactNode, not a label/handler pair.
      ...(finished || !isChecklistLive
        ? {}
        : {
            action: (
              <Button variant="text" onClick={() => navigate(ROUTES.HOME)}>
                View setup
              </Button>
            ),
          }),
    })
  }, [checklist.isLoading, checklist.completedCount, checklist.total, isChecklistLive, open, navigate])
}
